import React from 'react';
import { Button } from '@rentspree/component-2023.components.atoms.button';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';
import { Card } from '@rentspree/component-2023.components.organisms.card';

export interface ChecklistStep {
  number: number;
  title: string;
  subtitle?: string;
  description: string;
  isActive: boolean;
  isCompleted: boolean;
  buttonText?: string;
  onButtonClick?: () => void;
}

export interface PropertyDetailOverviewTabProps {
  title?: string;
  steps: ChecklistStep[];
  className?: string;
  style?: React.CSSProperties;
}

export const PropertyDetailOverviewTab: React.FC<
  PropertyDetailOverviewTabProps
> = ({ title = 'Leasing Checklist', steps, className, style }) => {
  return (
    <Card
      className={className || ''}
      style={{
        padding: 0,
        borderRadius: 12,
        border: '1px solid #f3f4f6',
        boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.05)',
        ...style,
      }}
      data-testid='leasing-checklist'
    >
      <div style={{ padding: '25px' }}>
        <Typography
          variant='h5'
          style={{
            fontWeight: 600,
            color: '#222222',
            marginBottom: 27,
          }}
        >
          {title}
        </Typography>

        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;

          if (step.isActive) {
            return (
              <div
                key={step.number}
                style={{
                  background: '#eff6ff',
                  border: '1px solid #dbeafe',
                  borderRadius: 12,
                  padding: '25px',
                  marginBottom: isLast ? 0 : 24,
                }}
                data-testid={`checklist-step-${step.number}-active`}
              >
                <div style={{ display: 'flex', gap: 12 }}>
                  <div
                    style={{
                      background: '#1f15e1',
                      color: '#ffffff',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {step.number}
                  </div>
                  <div style={{ flex: 1 }}>
                    <Typography
                      variant='h6'
                      style={{
                        fontWeight: 600,
                        color: '#222222',
                        marginBottom: 8,
                      }}
                    >
                      {step.title}
                    </Typography>
                    {step.subtitle && (
                      <Typography
                        variant='h6'
                        style={{
                          fontWeight: 600,
                          color: '#222222',
                          marginBottom: 12,
                        }}
                      >
                        {step.subtitle}
                      </Typography>
                    )}
                    <Typography
                      variant='body1'
                      style={{
                        color: '#6b7280',
                        marginBottom: step.buttonText ? 24 : 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {step.description}
                    </Typography>
                    {step.buttonText && (
                      <Button
                        variant='contained'
                        color='primary'
                        size='large'
                        onClick={step.onButtonClick}
                        style={{
                          background: '#1f15e1',
                          borderRadius: 8,
                          padding: '12.44px 24px',
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#ffffff',
                          textAlign: 'center',
                          fontFamily: 'Inter, sans-serif',
                        }}
                        data-testid={`checklist-step-${step.number}-button`}
                      >
                        {step.buttonText}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={step.number}
              style={{
                display: 'flex',
                gap: 12,
                opacity: 0.5,
                marginBottom: isLast ? 0 : 24,
              }}
              data-testid={`checklist-step-${step.number}-disabled`}
            >
              <div
                style={{
                  background: '#d1d5db',
                  color: '#6b7280',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {step.number}
              </div>
              <div style={{ flex: 1 }}>
                <Typography
                  variant='h6'
                  style={{
                    fontWeight: 500,
                    color: '#9ca3af',
                    marginBottom: 4,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant='body2'
                  style={{
                    color: '#9ca3af',
                    fontSize: '14px',
                  }}
                >
                  {step.description}
                </Typography>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
